import React from 'react';

import { Components, type Palette, Theme } from '@mui/material';
import { TypographyPropsVariantOverrides } from '@mui/material/Typography';
import { TypographyVariantsOptions } from '@mui/material/styles';

import { FontSizes } from '@/assets/fonts.ts';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    subtitleLarge: React.CSSProperties;
    subtitle: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    body: React.CSSProperties;
    bodySmall: React.CSSProperties;
    button: React.CSSProperties;
    buttonSmall: React.CSSProperties;
    caption: React.CSSProperties;
    captionSmall: React.CSSProperties;
  }

  interface TypeText {
    title: string;
  }
}

declare module '@mui/material/Typography' {
  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    subtitleLarge: React.CSSProperties;
    subtitle: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    body: React.CSSProperties;
    bodySmall: React.CSSProperties;
    button: React.CSSProperties;
    buttonSmall: React.CSSProperties;
    caption: React.CSSProperties;
    captionSmall: React.CSSProperties;
  }

  interface TypographyPropsVariantOverrides {
    h1: true;
    h2: true;
    h3: true;
    h4: true;
    h5: true;
    h6: true;

    subtitle1: false;
    subtitle2: false;
    subtitleLarge: true;
    subtitle: true;

    body1: false;
    body2: false;
    bodyLarge: true;
    body: true;
    bodySmall: true;

    button: true;
    buttonSmall: true;

    caption: true;
    captionSmall: true;

    overline: false;
  }
}

export type TypographyVariant = {
  [K in keyof TypographyPropsVariantOverrides]: TypographyPropsVariantOverrides[K] extends true
    ? K
    : never;
}[keyof TypographyPropsVariantOverrides];

export const TypographyComponentOverrides: Components<Theme>['MuiTypography'] =
  {
    defaultProps: {
      variantMapping: {
        subtitleLarge: 'p',
        subtitle: 'p',
        bodyLarge: 'p',
        body: 'p',
        bodySmall: 'p',
        button: 'p',
        buttonSmall: 'p',
        caption: 'p',
        captionSmall: 'p',
      },
    },
  };

export const defaultTypographyOptions: (
  palette: Palette,
) => TypographyVariantsOptions = palette => ({
  fontFamily: 'NunitoSans, Roboto, sans-serif',
  h1: { fontSize: FontSizes.h1, fontWeight: 700, color: palette.text.title },
  h2: { fontSize: FontSizes.h2, fontWeight: 700, color: palette.text.title },
  h3: { fontSize: FontSizes.h3, fontWeight: 700, color: palette.text.title },
  h4: { fontSize: FontSizes.h4, fontWeight: 700, color: palette.text.title },
  h5: { fontSize: FontSizes.h5, fontWeight: 700, color: palette.text.title },
  h6: { fontSize: FontSizes.h6, fontWeight: 700, color: palette.text.primary },
  subtitleLarge: {
    fontSize: FontSizes.subtitleLarge,
    fontWeight: 700,
    color: palette.text.primary,
  },
  subtitle: {
    fontSize: FontSizes.subtitle,
    fontWeight: 700,
    color: palette.text.primary,
  },
  bodyLarge: {
    fontSize: FontSizes.bodyLarge,
    fontWeight: 400,
    color: palette.text.primary,
  },
  body: {
    fontSize: FontSizes.body,
    fontWeight: 400,
    color: palette.text.primary,
  },
  bodySmall: {
    fontSize: FontSizes.bodySmall,
    fontWeight: 600,
    color: palette.text.primary,
  },
  button: {
    fontSize: FontSizes.button,
    fontWeight: 600,
    color: palette.text.primary,
    textTransform: 'none',
  },
  buttonSmall: {
    fontSize: FontSizes.buttonSmall,
    fontWeight: 600,
    color: palette.text.secondary,
    textTransform: 'none',
  },
  caption: {
    fontSize: FontSizes.caption,
    fontWeight: 600,
    color: palette.text.secondary,
  },
  captionSmall: {
    fontSize: FontSizes.captionSmall,
    fontWeight: 700,
    lineHeight: 1,
    color: palette.text.secondary,
  },
});
